import { PublicClientApplication } from '@azure/msal-browser';

// 認証モード: 'header'（dev暫定・X-User-Email）/ 'msal'（id_token）。
// 安全側の既定は 'header'。MSAL は VITE_AUTH_MODE=msal を明示した時だけ有効。
export const AUTH_MODE: 'header' | 'msal' =
  import.meta.env.VITE_AUTH_MODE === 'msal' ? 'msal' : 'header';

const clientId = import.meta.env.VITE_AADB2C_CLIENT_ID ?? '';
const authority = import.meta.env.VITE_AADB2C_AUTHORITY ?? '';
const knownAuthority = (() => {
  try {
    return new URL(authority).host;
  } catch {
    return '';
  }
})();

const LOGIN_REQUEST = { scopes: ['openid', 'profile', 'email'] };

let _msal: PublicClientApplication | null = null;

export function msalEnabled(): boolean {
  return AUTH_MODE === 'msal' && !!clientId && !!authority;
}

function instance(): PublicClientApplication {
  if (!_msal) {
    _msal = new PublicClientApplication({
      auth: {
        clientId,
        authority,
        knownAuthorities: knownAuthority ? [knownAuthority] : [],
        redirectUri: window.location.origin,
      },
      cache: { cacheLocation: 'sessionStorage' },
    });
  }
  return _msal;
}

/**
 * アプリ起動時に呼ぶ。リダイレクト応答を処理し、未ログインならログインへ送る。
 * 戻り値 false = ログインリダイレクト中（描画しない）。
 */
export async function initAuth(): Promise<boolean> {
  if (!msalEnabled()) return true;
  const msal = instance();
  await msal.initialize();
  const resp = await msal.handleRedirectPromise();
  if (resp?.account) msal.setActiveAccount(resp.account);

  const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0] ?? null;
  if (!account) {
    await msal.loginRedirect(LOGIN_REQUEST);
    return false;
  }
  msal.setActiveAccount(account);
  return true;
}

// トークン期限の判定に使う余裕（秒）。通信中に切れるのを避ける。
const EXP_SKEW_SEC = 60;

/** JWT の exp(UNIX秒) を取り出す。解析できなければ null。 */
function tokenExp(token: string): number | null {
  try {
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const claims = JSON.parse(atob(b64));
    return typeof claims.exp === 'number' ? claims.exp : null;
  } catch {
    return null;
  }
}

/** id_token が期限切れ（or 残り60秒未満）か。exp を読めない場合は有効扱い。 */
function isExpired(token: string): boolean {
  const exp = tokenExp(token);
  return exp !== null && exp - EXP_SKEW_SEC <= Date.now() / 1000;
}

/**
 * API 呼び出し用の id_token を取得。失敗時は再ログインへ送り null を返す。
 *
 * MSAL はキャッシュの有効判定を「アクセストークンの期限」で行うため、
 * 期限切れの id_token をそのまま返すことがある（例外にならない）。
 * その場合 API 側は 401「認証情報がありません」になるので、
 * ここで自前で exp を確認し、切れていれば強制更新する。
 */
export async function getIdToken(forceRefresh = false): Promise<string | null> {
  if (!msalEnabled()) return null;
  const msal = instance();
  const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
  if (!account) return null;
  try {
    let r = await msal.acquireTokenSilent({ ...LOGIN_REQUEST, account, forceRefresh });
    if (!forceRefresh && isExpired(r.idToken)) {
      r = await msal.acquireTokenSilent({ ...LOGIN_REQUEST, account, forceRefresh: true });
    }
    if (isExpired(r.idToken)) {
      // 強制更新しても切れている＝セッション自体が終了。再ログインへ。
      await msal.loginRedirect(LOGIN_REQUEST);
      return null;
    }
    return r.idToken;
  } catch {
    await msal.loginRedirect(LOGIN_REQUEST);
    return null;
  }
}

/** 再ログインへ送る（トークン更新に失敗した API 呼び出しからの復帰用）。 */
export async function relogin(): Promise<void> {
  if (!msalEnabled()) return;
  await instance().loginRedirect(LOGIN_REQUEST);
}

export function logout(): void {
  if (msalEnabled()) instance().logoutRedirect();
}
